import { setApiBaseUrl, setAgentId, setWidgetPublicBaseUrl } from '@/config/api';
import {
  isChubbEnabledByOption,
  setChubbLegacyConfig,
  resetChubbLegacyConfig,
  resolveChubbApiBaseUrl,
} from '@/config/chubbLegacy';
import { setMountSessionToken } from '@/utils/sessionToken';
import { setPartnerAuthOverride } from '@/utils/partnerAuth';
import { fetchCanEmitFromHelios } from '@/services/heliosAutosPermissions';
import type { WidgetOptions } from './types';

export interface AgentAuthResult {
  agentId: string | undefined;
  canEmit: boolean;
}

/** Apply the global (non-Vue) configuration implied by the mount options. */
export function applyMountConfig(options: WidgetOptions): void {
  if (options.apiBaseUrl) {
    setApiBaseUrl(options.apiBaseUrl);
  }

  if (options.widgetPublicUrl) {
    setWidgetPublicBaseUrl(options.widgetPublicUrl);
  }

  setMountSessionToken(options.token);

  setPartnerAuthOverride({ partnerId: options.partnerId, partnerApiKey: options.partnerApiKey });

  // Chubb in the Qualitas/ANA comparator (served by this widget's own API). Off unless the host opts in.
  if (isChubbEnabledByOption(options.enableChubb)) {
    setChubbLegacyConfig({
      enabled: true,
      apiBaseUrl: resolveChubbApiBaseUrl(options.chubbApiBaseUrl),
    });
  } else {
    resetChubbLegacyConfig();
  }
}

/** Authenticate the widget when `guidUsuario` is given; failures are logged and ignored. */
export async function resolveAgentAuth(options: WidgetOptions): Promise<AgentAuthResult> {
  const result: AgentAuthResult = { agentId: options.agentId, canEmit: false };
  if (!options.guidUsuario) return result;

  try {
    const { authenticateWidget } = await import('@/composables/useWidgetAuth');
    const agentData = await authenticateWidget(options.guidUsuario);

    if (agentData) {
      result.agentId = String(agentData.Id_Agente);
      result.canEmit = await fetchCanEmitFromHelios(agentData.Id_Usuario);
    }
  } catch (error) {
    console.error('❌ Error al autenticar widget:', error);
    // Continue without authentication
  }
  return result;
}

function applyUpdateSettings(newOptions: WidgetOptions, mountOptions: WidgetOptions): void {
  if (newOptions.apiBaseUrl) {
    setApiBaseUrl(newOptions.apiBaseUrl);
  }
  if (newOptions.widgetPublicUrl) {
    setWidgetPublicBaseUrl(newOptions.widgetPublicUrl);
  }
  if (newOptions.token !== undefined) {
    setMountSessionToken(newOptions.token);
  }
  if (newOptions.partnerId !== undefined || newOptions.partnerApiKey !== undefined) {
    setPartnerAuthOverride({ partnerId: newOptions.partnerId, partnerApiKey: newOptions.partnerApiKey });
  }
  if (newOptions.enableChubb === true) {
    setChubbLegacyConfig({
      enabled: true,
      apiBaseUrl: resolveChubbApiBaseUrl(newOptions.chubbApiBaseUrl ?? mountOptions.chubbApiBaseUrl),
    });
  } else if (newOptions.enableChubb === false) {
    resetChubbLegacyConfig();
  }
}

async function applyUpdateIdentity(newOptions: WidgetOptions): Promise<void> {
  if (newOptions.guidUsuario) {
    const { useWidgetAuth } = await import('@/composables/useWidgetAuth');
    const widgetAuth = useWidgetAuth();
    await widgetAuth.initialize(newOptions.guidUsuario);
  } else if (newOptions.agentId) {
    setAgentId(newOptions.agentId);
  }
}

/** Apply the non-style part of `updateConfig`. */
export async function applyUpdateConfig(
  newOptions: WidgetOptions,
  mountOptions: WidgetOptions,
): Promise<void> {
  applyUpdateSettings(newOptions, mountOptions);
  await applyUpdateIdentity(newOptions);
}
